import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Volume2, ChevronDown, Play, Mic, Music, Monitor } from "lucide-react";

/**
 * Audio dropdown menu for song cards.
 * Desktop: positioned dropdown beneath the button.
 * Mobile: bottom sheet slide-up.
 *
 * Menu items: Player, Record, Tele, Music
 */
const AudioDropdownMenu = ({
  song,
  isMobile = false,
  hasAudio = false,
  isPlayerOpen = false,
  isRecordOpen = false,
  onTogglePlayer,
  onToggleRecord,
  onUploadMusic,
  isUploading = false,
  // Teleprompter props
  isTeleprompterOpen = false,
  onToggleTeleprompter = null,
  hasTeleprompter = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click (desktop)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isMobile]);

  const handleItemClick = (action) => {
    action();
    setIsOpen(false);
  };

  const menuItems = [
    {
      key: "player",
      label: "Player",
      Icon: Play,
      active: isPlayerOpen,
      disabled: !hasAudio,
      disabledLabel: "No audio",
      onClick: () => onTogglePlayer && onTogglePlayer(song),
    },
    {
      key: "record",
      label: "Record",
      Icon: Mic,
      active: isRecordOpen,
      onClick: () => onToggleRecord && onToggleRecord(song),
    },
    {
      key: "teleprompter",
      label: "Tele",
      Icon: Monitor,
      active: isTeleprompterOpen,
      disabled: !hasTeleprompter,
      disabledLabel: "No lyrics",
      onClick: () => onToggleTeleprompter && onToggleTeleprompter(song),
    },
    {
      key: "music",
      label: isUploading ? "Uploading..." : hasAudio ? "Replace" : "Add Audio",
      Icon: Music,
      disabled: isUploading,
      onClick: () => onUploadMusic && onUploadMusic(song),
    },
  ];

  // Position the portal dropdown beneath the button
  const [menuPos, setMenuPos] = useState(null);

  const toggleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      setMenuPos(null);
    } else {
      // Compute position synchronously before opening so the portal
      // never renders at (0,0)
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.left });
      }
      setIsOpen(true);
    }
  };

  // Desktop dropdown (portal to body so it escapes overflow:hidden)
  if (!isMobile) {
    return (
      <div className="relative inline-flex">
        <button
          ref={buttonRef}
          onClick={toggleOpen}
          className={`text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors inline-flex items-center gap-1 ${
            isPlayerOpen || isRecordOpen || isTeleprompterOpen
              ? "bg-forest-600 text-gold-500 border border-gold-500/30"
              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
          }`}
          title="Audio menu"
        >
          <Volume2 className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
          <span className="hidden md:inline">Audio</span>
          <ChevronDown
            className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen &&
          menuPos &&
          createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                zIndex: 99999,
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
              }}
            >
              <div
                className="bg-forest-800 border border-forest-700 rounded-lg shadow-xl"
                style={{ display: "block", padding: "4px" }}
              >
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() =>
                      !item.disabled && handleItemClick(item.onClick)
                    }
                    disabled={item.disabled}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      width: "100%",
                      textAlign: "left",
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: item.key !== "music" ? "2px" : "0",
                    }}
                    className={`transition-colors ${
                      item.disabled
                        ? "opacity-40 cursor-not-allowed bg-forest-700 text-forest-400"
                        : item.active
                          ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                          : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                    }`}
                  >
                    <item.Icon
                      style={{ width: "16px", height: "16px", flexShrink: 0 }}
                      className="text-gold-500"
                    />
                    <span>
                      {item.disabled && item.disabledLabel
                        ? item.disabledLabel
                        : item.label}
                    </span>
                    {item.active && (
                      <span className="ml-auto text-gold-500 text-xs">●</span>
                    )}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )}
      </div>
    );
  }

  // Mobile slide-up panel
  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs text-forest-200 hover:text-forest-100 inline-flex items-center gap-1"
      >
        <Volume2 className="w-3.5 h-3.5 text-gold-500" />
        <span>Audio</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99998,
                background: "rgba(0,0,0,0.3)",
              }}
              onClick={() => setIsOpen(false)}
            />
            {/* Slide-up panel above navbar */}
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                bottom:
                  "max(84px, calc(68px + env(safe-area-inset-bottom, 0px)))",
                left: 0,
                right: 0,
                zIndex: 99999,
                animation: "audioSlideUp 0.2s ease-out",
              }}
            >
              <div
                className="bg-forest-800 border-t border-forest-700"
                style={{ display: "block", padding: "6px 8px" }}
              >
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() =>
                      !item.disabled && handleItemClick(item.onClick)
                    }
                    disabled={item.disabled}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      width: "100%",
                      fontSize: "14px",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      marginBottom: item.key !== "music" ? "4px" : "0",
                    }}
                    className={`transition-colors ${
                      item.disabled
                        ? "opacity-40 cursor-not-allowed bg-forest-700 text-forest-400"
                        : item.active
                          ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                          : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                    }`}
                  >
                    <item.Icon
                      style={{ width: "20px", height: "20px", flexShrink: 0 }}
                      className="text-gold-500"
                    />
                    <span>
                      {item.disabled && item.disabledLabel
                        ? item.disabledLabel
                        : item.label}
                    </span>
                    {item.active && (
                      <span className="ml-auto text-gold-500 text-sm">●</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <style>{`
              @keyframes audioSlideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}</style>
          </>,
          document.body,
        )}
    </>
  );
};

export default AudioDropdownMenu;
